<template>
  <div>
    <v-container fluid grid-list-md>
      <v-layout wrap align-center>
        <v-flex xs12 sm4 md4 d-flex>
          <v-text-field
              v-model="filters.name"
              append-icon="search"
              :label="`${trans('app.search.search')} (${trans('app.type_at_least_n_characters', {count: 3})})`"
              single-line
              hide-details
              solo
              clearable
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4 md4 d-flex>
          <v-autocomplete
              v-model="selectedProducts"
              :items="products"
              :search-input.sync="productSearch"
              :clearable="true"
              :loading="productsLoading"
              :placeholder="`app.pages.products.products` | trans"
              :hide-selected="true"
              :deletable-chips="true"
              item-text="text"
              item-value="id"
              multiple
              hide-details
              :no-data-text="hint"
              return-object
              no-filter
              chips
              solo
              dense
          >
            <template v-slot:item="data">
              <template>
                {{ data.item.text }}
                <template v-if="data.item.shared">
                  <v-tooltip right>
                    <template #activator="{ on }">
                      <v-icon v-on="on" small color="success">share</v-icon>
                    </template>
                    <span>{{ trans('app.sharing.shared_by', {name: data.item.owned_by}) }}</span>
                  </v-tooltip>
                </template>
              </template>
            </template>
          </v-autocomplete>
        </v-flex>
        <v-flex xs12 sm4 md4 d-flex>
          <v-combobox
              multiple
              clearable
              :items="meals"
              :label="trans('app.foods.meal')"
              v-model="selectedMeals"
              :search-input.sync="mealsSearch"
              hide-details
              solo
              dense
          ></v-combobox>
        </v-flex>
        <v-flex xs12 sm4 md4 d-flex>
          <v-combobox
              multiple
              clearable
              :items="diets"
              :label="trans('app.foods.diet')"
              v-model="selectedDiets"
              :search-input.sync="dietsSearch"
              hide-details
              solo
              dense
          ></v-combobox>
        </v-flex>
        <v-flex xs12 sm4 md4 d-flex>
          <v-combobox
              multiple
              clearable
              :items="tags"
              :label="trans('app.foods.tag')"
              v-model="selectedTags"
              :search-input.sync="tagsSearch"
              hide-details
              solo
              dense
          ></v-combobox>
        </v-flex>
        <v-flex xs12 sm4 md4 d-flex>
          <v-combobox
              multiple
              clearable
              :items="equipments"
              :label="trans('app.foods.equipment')"
              v-model="selectedEquipments"
              :search-input.sync="equipmentsSearch"
              hide-details
              solo
              dense
          ></v-combobox>
        </v-flex>
      </v-layout>
    </v-container>
    <v-data-table
        :disable-initial-sort="true"
        :headers="headers"
        :items="items"
        :pagination.sync="pagination"
        :total-items="total"
        :loading="isLoading"
        :rows-per-page-items="[10,25,50,100]"
        :no-data-text="trans('app.search.no_data')"
    >
      <template v-slot:headerCell="{header}">
        <v-tooltip top v-if="header.tooltip">
            <span slot="activator">
              {{ header.text }}
            </span>
          <span>
              {{ header.tooltip }}
            </span>
        </v-tooltip>
        <span v-else>
          {{ header.text }}
        </span>
      </template>
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template v-slot:items="{item}">
        <tr>
          <td class="p-0">
            <v-layout align-center justify-center>
              <icon-button
                  :permission="canView(item)"
                  :icon="expandedFoodId === item.id ? 'indeterminate_check_box' : 'add_box'"
                  color="success"
                  cls="mr-0 ml-0"
                  @click="toggleDetails(item)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.foods.show_details' | trans }}
              </icon-button>
            </v-layout>
          </td>
          <td>
            {{ item.name }}
            <template v-if="item.shared">
              <v-tooltip right>
                <template #activator="{ on }">
                  <v-icon v-on="on" small color="success">share</v-icon>
                </template>
                <span>{{ trans('app.sharing.shared_by', {name: item.owned_by}) }}</span>
              </v-tooltip>
            </template>
          </td>
          <td>
            {{ item.portions }}
          </td>
          <td>
            {{ item.kcal | round(0) }}
          </td>
          <td>
            {{ item.carbo | round(0) }}
          </td>
          <td>
            {{ item.protein | round(0) }}
          </td>
          <td>
            {{ item.fats | round(0) }}
          </td>
          <td></td>
          <td class="justify-end layout px-0 pr-4">
            <v-layout align-center justify-end>
              <icon-button
                  icon="launch"
                  color="light-blue"
                  cls="mr-0 ml-0"
                  @click="show(item.id)"
                  :permission="canView(item)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.open' | trans }}
              </icon-button>
              <icon-button
                  icon="edit"
                  cls="mr-0 ml-0"
                  :color="canEdit(item)? 'success' : 'grey'"
                  @click="edit(item.id)"
                  :permission="canEdit(item)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
                  :no-permission-tool-tip="(item.is_system && !hasPermission('food_system_edit') || item.shared) ? 'app.edit' : 'app.no_access_tooltip'"
              >
                {{
                  'app.edit' | trans
                }}
              </icon-button>
              <icon-button
                  icon="content_copy"
                  color="success"
                  cls="mr-0 ml-0"
                  :permission="canCopy(item)"
                  @click="copy(item.id)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.copy' | trans }}
              </icon-button>
              <icon-button
                  v-if="item.shared === false"
                  @click="favorite(item)"
                  :icon="item.is_favourite ? 'favorite' : 'favorite_border'"
                  color="amber"
                  cls="mr-0 ml-0"
                  :permission="canFavourite(item)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ item.is_favourite ? 'app.favorited' : 'app.add_to_favorites' | trans }}
              </icon-button>
              <icon-button
                  icon="delete"
                  :color="item.can_delete ? 'red' : 'grey'"
                  cls="mr-0 ml-0"
                  @click="deleteItem(item)"
                  :permission="item.can_delete"
                  :no-permission-tool-tip="(item.is_system && !hasPermission('food_system_delete') || item.shared) ? 'app.delete' : 'app.no_access_tooltip'"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.delete' | trans }}
              </icon-button>
            </v-layout>
          </td>
        </tr>
        <food-quick-details :food-id="item.id"/>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import debounce from "lodash/debounce";
import {stringify} from "qs";
import {trans} from "../../app";
import {FORMAT_DATETIME} from "../../utils/dateFormatter";
import {mapActions, mapGetters, mapState} from "vuex";
import {DIETITIAN_FREE, DIETITIAN_TEST} from "../../utils/roles";

export default {
  props: {
    group: String,
  },
  data: () => ({
    FORMAT_DATETIME: FORMAT_DATETIME,
    filters: {
      name: '',
    },
    pagination: {
      rowsPerPage: 50,
      sortBy: 'name',
      descending: false,
    },
    total: 0,
    items: [],
    isLoading: true,
    pages: 0,
    expandedFoodId: null,
    selectedProducts: [],
    products: [],
    productSearch: null,
    currentTimeout: null,
    productsLoading: false,
    hint: trans('app.type_at_least_n_characters', {count: 3}),
    selectedMeals: [],
    selectedDiets: [],
    selectedTags: [],
    selectedEquipments: [],
    mealsSearch: null,
    dietsSearch: null,
    tagsSearch: null,
    equipmentsSearch: null,
  }),
  created() {
    this.debounceFetch = debounce(this.fetchData, 500);
  },
  computed: {
    ...mapState(['dictionaries']),
    ...mapGetters(['hasPermission', 'userRole', 'ownFoodsCount']),
    headers() {
      return [
        {
          text: '', align: 'center', sortable: false
        },
        {
          text: trans('app.name'), align: 'left', value: 'name', sortable: true,
        },
        {
          text: trans('app.foods.portions'), align: 'left', value: 'portions', sortable: true,
        },
        {
          text: 'Kcal', align: 'left', value: 'kcal', sortable: true, tooltip: trans('app.nutrients.kcal'),
        },
        {
          text: 'W (g)', align: 'left', value: 'carbo', sortable: true, tooltip: trans('app.nutrients.carbo'),
        },
        {
          text: 'B (g)', align: 'left', value: 'protein', sortable: true, tooltip: trans('app.nutrients.protein'),
        },
        {
          text: 'T (g)', align: 'left', value: 'fats', sortable: true, tooltip: trans('app.nutrients.fats'),
        },
        {
          text: trans('app.foods.created_at'), align: 'left', value: 'created_at', sortable: true
        },
        {
          text: '', align: 'center', sortable: false,
        },
      ];
    },
    meals() {
      return this.dictionaries.food_tags.meals.map(meal => {
        return {
          text: meal.name,
          value: meal.id,
        };
      });
    },
    diets() {
      return this.dictionaries.food_tags.diets.map(meal => {
        return {
          text: meal.name,
          value: meal.id,
        };
      });
    },
    tags() {
      return this.dictionaries.food_tags.tags.map(tag => {
        return {
          text: tag.name,
          value: tag.id,
        };
      })
    },
    equipments() {
      return this.dictionaries.food_tags.equipments.map(equipment => {
        return {
          text: equipment.name,
          value: equipment.id,
        };
      })
    },
  },
  watch: {
    'filters.name': {
      handler(name) {
        if (name === null || name === '' || name.length >= 3) {
          this.debounceFetch();
        }
      },
      deep: true,
    },
    pagination: {
      handler(newValue, oldValue) {
        if (oldValue.page !== undefined) {
          this.fetchData();
        }
      },
      deep: true,
    },
    expandedFoodId: {
      handler() {
        this.$root.$emit('show-food-details', this.expandedFoodId);
      }
    },
    async productSearch(val) {
      this.clearTimeoutIfExists();
      if (!val) {
        this.products = [...this.selectedProducts];
        this.productsLoading = false;
        return;
      }
      this.hint = val.length < 3 ? trans('app.type_at_least_n_characters', {count: 3}) : undefined;
      if (val.length < 3) {
        this.products = [...this.selectedProducts];
        this.productsLoading = false;
        return;
      }
      this.productsLoading = true;
      this.currentTimeout = setTimeout(async () => {
        const body = {
          q: val,
        };
        const {data} = await axios.post(`/foods/all-search.json`, body);
        this.products = [...data, ...this.selectedProducts];
        this.productsLoading = false;
        this.currentTimeout = null;
      }, 500);
    },
    selectedProducts: {
      handler() {
        this.debounceFetch();
        this.productSearch = null;
        this.products = [...this.selectedProducts];
      },
      deep: true
    },
    selectedMeals: {
      handler() {
        this.debounceFetch();
        this.mealsSearch = null;
      },
      deep: true
    },
    selectedDiets: {
      handler() {
        this.debounceFetch();
        this.dietsSearch = null;
      },
      deep: true
    },
    selectedTags: {
      handler() {
        this.debounceFetch();
        this.tagsSearch = null;
      },
      deep: true
    },
    selectedEquipments: {
      handler() {
        this.debounceFetch();
        this.equipmentsSearch = null;
      },
      deep: true
    },
  },
  methods: {
    ...mapActions(['fetchOwnFoodsCount']),
    async fetchData() {
      this.expandedFoodId = null;
      this.isLoading = true;
      const {sortBy, descending, page, rowsPerPage} = this.pagination;

      const query = {
        group: this.group,
        page: page,
        perPage: rowsPerPage,
        orderBy: sortBy,
        dir: descending ? 'desc' : 'asc',
        products: this.selectedProducts.map(product => product.id),
        meals: this.selectedMeals.map(meal => meal.value),
        diets: this.selectedDiets.map(diet => diet.value),
        tags: this.selectedTags.map(tag => tag.value),
        equipments: this.selectedEquipments.map(equipment => equipment.value),
        ...this.filters,
      };

      const response = await axios.get(`/v2/foods?${stringify(query, {skipNulls: true})}`);

      const {data, meta} = response.data;
      this.items = data;
      this.total = meta.total;
      this.pages = meta.last_page;
      this.$root.$emit('invalidate-food-details-cache');
      this.isLoading = false;
    },
    async toggleDetails(item) {
      if (this.expandedFoodId === item.id) {
        this.expandedFoodId = null;
      } else {
        this.expandedFoodId = item.id;
      }
    },
    canView(item) {
      return item.can_view;
    },
    canFavourite(item) {
      return item.can_favourite;
    },
    canEdit(item) {
      if (item.is_system && this.hasPermission('food_system_edit')) {
        return true;
      }
      if (item.shared && this.hasPermission('food_system_edit')) {
        return true;
      }
      return item.can_edit;
    },
    canCopy(item) {
      if (item.is_system && this.hasPermission('food_system_edit')) {
        return true;
      }
      if ((this.userRole === DIETITIAN_FREE || this.userRole === DIETITIAN_TEST) && this.ownFoodsCount >= 20) {
        return false;
      }
      return item.can_copy;
    },
    deleteItem(item) {
      this.$confirmDanger(
          trans('app.foods.confirm_removal', {name: item.name}),
          trans('app.remove') + ' ' + trans('app.foods.food').toLowerCase(),
      ).then(async () => {
        const url = `/v2/foods/${item.id}`;
        await axios.delete(url);
        await this.fetchData();
        await this.fetchOwnFoodsCount();
        this.$toast(trans('app.actions.removed', {name: trans('app.foods.food').toLowerCase()}));
      }).catch((error) => {
        this.sentryCaptureException(error);
      })
    },
    async favorite(item) {
      if (this.group === 'favorite') {
        await axios.delete(`/v2/foods/${item.id}/favourites`);
        this.$toast(trans('app.removed_from_favorites'));
      } else {
        const response = await axios.post(`/v2/foods/${item.id}/favourites`);
        item.is_favourite = response.data.is_favourite;
        this.$toast(item.is_favourite ? trans('app.added_to_favorites') : trans('app.removed_from_favorites'));
      }
      await this.fetchData();
    },
    resetSearch() {
      this.filters = {
        name: '',
      };
      this.pagination.rowsPerPage = 50;
      this.pagination.sortBy = 'name';
      this.pagination.descending = false;
      this.selectedProducts = [];
      this.products = [];
      this.productSearch = null;
      this.selectedMeals = [];
      this.selectedDiets = [];
      this.selectedTags = [];
      this.selectedEquipments = [];
    },
    clearTimeoutIfExists() {
      if (this.currentTimeout !== null) {
        clearTimeout(this.currentTimeout);
        this.currentTimeout = null;
      }
    },
    show(id) {
        this.$emit('show', {id: id});
      },
    edit(id) {
      this.$emit('edit', {id: id});
    },
    async copy(id) {
      const {data} = await axios.post(`/v2/foods/${id}/duplicate`);
      this.$emit('edit', {id: data.id});
    }
  },
}
</script>
<style scoped>
>>> table.v-table thead th:nth-child(1) {
  width: 20px;
  padding-right: 0;
}

>>> table.v-table tbody tr td:nth-child(1) {
  width: 20px;
  padding-right: 0;
}

>>> .v-chip .v-chip__content {
  height: 25px;
}

>>> .icheckbox_square-green {
  display: none;
}
</style>
